// @ts-nocheck - Tipos do Supabase desatualizados após migration
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useContextValidation } from "@/hooks/useContextValidation";
import { useInvalidateAllPedidoQueries } from "@/hooks/useInvalidateAllPedidoQueries";
import { useAuth } from "@/hooks/useAuth";
import { validarItemParaFechar } from "@/lib/validacaoPedido";
import { getTriagemDaColuna } from "@/lib/quadro-utils";
import { exigirInputFixaAprovado, itensTemFixa } from "@/lib/inputFixaConferencia";


type QuadroEnum = "COMERCIAL" | "CONTRATO_PRIME" | "TRAMITACAO_VIVO" | "QUALIDADE";

type MoveToArgs = {
  pedidoId: string;
  itemId?: string;
  quadroDestino: QuadroEnum;
  colunaDestino: string;
  statusIdDestino?: string | null;
  motivo?: string | null;
  origemTriagem?: boolean;
  // Restauração de snapshot (retomada de pedido recuperado): volta pro EXATO
  // quadro/coluna de onde foi cancelado. Não é transição nova, então pula a
  // validação de grafo de transição (senão voltar pra PRE_ATIVO era barrado).
  skipValidacao?: boolean;
};

export function usePedidoMovement() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { validateRequiredFields } = useContextValidation();
  const { invalidateAll } = useInvalidateAllPedidoQueries();
  const { profile } = useAuth();
  const userRole = profile?.role;
  

  const moveToMutation = useMutation({
    onMutate: async (variables: MoveToArgs) => {
      // ✅ Cancelar refetches em andamento (todas as queries de kanban, independente de filtros)
      await qc.cancelQueries({ queryKey: ['pedido-itens-completo', variables.pedidoId] });
      await qc.cancelQueries({ queryKey: ['pedido-detail', variables.pedidoId] });
      await qc.cancelQueries({ queryKey: ['kanban', variables.quadroDestino], exact: false });
      await qc.cancelQueries({ queryKey: ['kanban-optimized'], exact: false });
      
      // ✅ Snapshot do estado anterior para rollback (TODAS as queries de kanban)
      const previousItens = qc.getQueryData(['pedido-itens-completo', variables.pedidoId]);
      const previousPedidoDetail = qc.getQueryData(['pedido-detail', variables.pedidoId]);
      const previousKanbanQueries = qc.getQueryCache()
        .findAll({ queryKey: ['kanban'] })
        .map(query => ({
          queryKey: query.queryKey,
          data: query.state.data
        }));
      
      // ✅ ATUALIZAÇÃO OTIMISTA - UI muda ANTES do servidor responder
      if (variables.itemId) {
        // Atualizar query de itens
        qc.setQueryData(['pedido-itens-completo', variables.pedidoId], (old: any) => {
          if (!old) return old;
          return old.map((item: any) => 
            item.id === variables.itemId 
              ? { 
                  ...item, 
                  quadro_atual: variables.quadroDestino,
                  coluna_atual: variables.colunaDestino,
                  status_nome: variables.colunaDestino, // ✅ Atualizar nome do status visualmente
                }
              : item
          );
        });

        // ✅ Atualizar query pedido-detail (usado no PainelPedido)
        qc.setQueryData(['pedido-detail', variables.pedidoId], (old: any) => {
          if (!old?.itens_pedido) return old;
          
          return {
            ...old,
            itens_pedido: old.itens_pedido.map((item: any) => 
              item.id === variables.itemId
                ? {
                    ...item,
                    // Atualizar campos DIRETOS do item
                    quadro_atual: variables.quadroDestino,
                    coluna_atual: variables.colunaDestino,
                    status_nome: variables.colunaDestino,
                    substatus_nome: variables.colunaDestino,
                    // Atualizar também o objeto PEDIDO aninhado (se existir)
                    pedido: item.pedido ? {
                      ...item.pedido,
                      quadro_atual: variables.quadroDestino,
                      coluna_atual: variables.colunaDestino,
                      status: item.pedido.status ? {
                        ...item.pedido.status,
                        id: variables.statusIdDestino,
                        nome: variables.colunaDestino,
                      } : undefined
                    } : undefined
                  }
                : item
            )
          };
        });
        
        // ✅ ATUALIZAÇÃO OTIMISTA DO KANBAN - Atualizar TODAS as queries (independente de filtros)
        const kanbanQueries = qc.getQueryCache().findAll({ queryKey: ['kanban', variables.quadroDestino] });
        kanbanQueries.forEach(query => {
          qc.setQueryData(query.queryKey, (old: any) => {
            if (!old?.columns) return old;
            
            // Remover o card da coluna antiga
            const columns = old.columns.map((col: any) => ({
              ...col,
              cards: col.cards.filter((c: any) => c.id !== variables.itemId)
            }));
            
            // Encontrar e atualizar o card na coluna nova
            const targetColumn = columns.find((col: any) => col.name === variables.colunaDestino);
            if (targetColumn) {
              const updatedCard = old.columns
                .flatMap((col: any) => col.cards)
                .find((c: any) => c.id === variables.itemId);
              
              if (updatedCard) {
                targetColumn.cards.push({
                  ...updatedCard,
                  quadro_atual: variables.quadroDestino,
                  coluna_atual: variables.colunaDestino
                });
              }
            }
            
            return { ...old, columns };
          });
        });

        // ✅ ATUALIZAÇÃO OTIMISTA DO KANBAN-OPTIMIZED
        const kanbanOptQueries = qc.getQueryCache().findAll({ queryKey: ['kanban-optimized'] });
        kanbanOptQueries.forEach(query => {
          qc.setQueryData(query.queryKey, (old: any) => {
            if (!old?.columns) return old;
            
            const columns = old.columns.map((col: any) => ({
              ...col,
              cards: col.cards.filter((c: any) => c.id !== variables.itemId)
            }));
            
            const targetColumn = columns.find((col: any) => col.name === variables.colunaDestino);
            if (targetColumn) {
              const updatedCard = old.columns
                .flatMap((col: any) => col.cards)
                .find((c: any) => c.id === variables.itemId);
              
              if (updatedCard) {
                targetColumn.cards.push({
                  ...updatedCard,
                  quadro_atual: variables.quadroDestino,
                  coluna_atual: variables.colunaDestino
                });
              }
            }
            
            return { ...old, columns };
          });
        });
      }
      
      // ✅ Toast IMEDIATO - não espera mutation
      toast({ title: "✅ Atualizando status...", duration: 1000 });
      
      return { previousItens, previousPedidoDetail, previousKanbanQueries };
    },
    mutationFn: async (args: MoveToArgs) => {
      let { pedidoId, quadroDestino, colunaDestino, statusIdDestino = null, motivo = null } = args;

      console.log('🔄 [usePedidoMovement] Iniciando movimentação:', {
        pedidoId,
        itemId: args.itemId,
        para: `${quadroDestino}:${colunaDestino}`,
        statusIdDestino
      });

      try {

      // Fechamento não é uma troca de status. O botão oficial valida a
      // proposta inteira e preserva a estrutura pedido-mãe + filhos.
      if (quadroDestino === 'CONTRATO_PRIME' && colunaDestino === 'PEDIDO FECHADO' && userRole !== 'master') {
        throw new Error('Use o botão oficial de fechar pedido. O status não pode ser aplicado manualmente.');
      }
      if (args.itemId && quadroDestino === 'CONTRATO_PRIME' && colunaDestino === 'PEDIDO FECHADO') {
        const validacao = await validarItemParaFechar(args.itemId);
        if (!validacao.valido) {
          throw new Error(`Item incompleto! Preencha os campos obrigatórios: ${validacao.camposFaltantes.join(', ')}`);
        }
      }

      // ✅ FALLBACK: Se statusIdDestino não foi fornecido, buscar automaticamente
      if (!statusIdDestino) {
        console.log('🔍 [usePedidoMovement] statusIdDestino não fornecido, buscando automaticamente...');
        const { data: statusAuto, error: statusAutoError } = await supabase
          .from('status_catalogo')
          .select('id')
          .eq('quadro', quadroDestino)
          .eq('coluna', colunaDestino)
          .eq('ativo', true)
          .limit(1)
          .maybeSingle();

        if (statusAutoError) throw statusAutoError;
        if (statusAuto) {
          statusIdDestino = statusAuto.id;
          console.log('✅ [usePedidoMovement] statusIdDestino encontrado:', statusIdDestino);
        } else {
          console.error('❌ [usePedidoMovement] Nenhum status ativo encontrado para:', { quadroDestino, colunaDestino });
        }
      }

      // 🛡️ VALIDAR que statusIdDestino é um UUID válido (formato básico)
      if (statusIdDestino && typeof statusIdDestino === 'string') {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!uuidRegex.test(statusIdDestino)) {
          console.error('❌ [usePedidoMovement] statusIdDestino não é um UUID válido:', statusIdDestino);
          throw new Error(`ID de status inválido: ${statusIdDestino}`);
        }
      }

      // 🚫 REGRA SUPREMA: Validar se o pedido pode retornar ao comercial
      const { data: podeRetornar, error: validacaoError } = await supabase.rpc('validar_retorno_comercial', {
        p_pedido_id: pedidoId,
        p_quadro_destino: quadroDestino
      });

      if (validacaoError) throw validacaoError;
      
      if (podeRetornar === false) {
        throw new Error(
          '🚫 BLOQUEADO: Este pedido já foi contabilizado como venda e não pode retornar para negociação. ' +
          'Esta regra protege a integridade dos relatórios de vendas e métricas comerciais.'
        );
      }

      // Verificar role do usuário (usar contexto para evitar query extra)
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      // Buscar o nome do status de destino para permitir fechamento para todos
      const { data: statusDestino } = await supabase
        .from('status_catalogo')
        .select('nome')
        .eq('id', statusIdDestino)
        .maybeSingle();

      const nomeStatusDestino = statusDestino?.nome || '';
      const statusFechamento = ['PEDIDO FECHADO', 'ACEITE REALIZADO'];
      
      // ✅ PERMITIR que QUALQUER cargo mova para status de fechamento
      // ✅ BKO e Qualidade têm acesso total a todos os quadros
      if (!statusFechamento.includes(nomeStatusDestino)) {
        // Apenas consultores mantêm restrição de quadro
        if (userRole === 'consultor' && quadroDestino !== 'COMERCIAL') {
          throw new Error('Consultores só podem movimentar itens no quadro COMERCIAL (exceto para fechamento de vendas)');
        }
        // BKO e Qualidade não têm mais restrições de quadro
      }

      const { data: pedidoAtual, error: fetchErr } = await supabase
        .from("pedidos")
        .select("cliente_id, version, status_agregado")
        .eq("id", pedidoId)
        .single();
      if (fetchErr || !pedidoAtual) throw new Error("Pedido não encontrado");

      // Validar bloqueio de role (exceto para MASTER)
      if (userRole !== 'master' && statusIdDestino) {
        const { data: statusDestino } = await supabase
          .from('status_catalogo')
          .select('roles_bloqueadas')
          .eq('id', statusIdDestino)
          .single();

        const rolesBloqueadas = (statusDestino?.roles_bloqueadas || []) as string[];
        if (rolesBloqueadas.includes(userRole || '')) {
          throw new Error(`Você não tem permissão para mover pedidos para este status. Entre em contato com o gestor.`);
        }
      }

      console.log('🔍 [usePedidoMovement] Chamando validar_transicao_status:', {
        p_pedido_id: pedidoId,
        p_quadro_dest: quadroDestino,
        p_coluna_dest: colunaDestino,
        p_status_dest: statusIdDestino,
        p_status_dest_type: typeof statusIdDestino
      });

      const { data: transicaoValida, error: rpcErr } = await supabase.rpc("validar_transicao_status", {
        p_pedido_id: pedidoId,
        p_quadro_dest: quadroDestino,
        p_coluna_dest: colunaDestino,
        p_status_dest: statusIdDestino || null,
      });

      console.log('📊 [usePedidoMovement] Resultado validar_transicao_status:', {
        transicaoValida,
        transicaoValida_type: typeof transicaoValida,
        transicaoValida_is_null: transicaoValida === null,
        transicaoValida_is_undefined: transicaoValida === undefined,
        rpcErr
      });

      if (rpcErr && !args.skipValidacao) {
        console.error('❌ [usePedidoMovement] RPC Error:', rpcErr);
        throw rpcErr;
      }

      // ✅ Verificação EXPLÍCITA para evitar false positives.
      // skipValidacao: restauração de snapshot (retomada) volta pro estado EXATO
      // anterior ao cancelamento — não passa pelo grafo de transição.
      if (transicaoValida !== true && !args.skipValidacao) {
        throw new Error(`🚫 Transição não permitida. Valor retornado: ${transicaoValida} (tipo: ${typeof transicaoValida})`);
      }

      console.log('✅ [usePedidoMovement] Transição validada com sucesso!');

      const contexto = `${quadroDestino}:${colunaDestino}`;
      const { isValid } = await validateRequiredFields("pedido", contexto, {
        pedidoId, quadroDestino, colunaDestino, statusIdDestino,
      });
      if (!isValid) throw new Error("Campos obrigatórios pendentes para essa transição.");

      // 5. Atualizar ITEM (itemId é SEMPRE obrigatório)
      if (!args.itemId) {
        throw new Error("itemId é obrigatório para mover itens. O sistema não move pedidos inteiros.");
      }

      // Atualizar apenas o ITEM específico
      const { data: itemAtual, error: itemFetchErr } = await supabase
        .from("itens_pedido")
        .select("quadro_atual, coluna_atual")
        .eq("id", args.itemId)
        .single();
      
      if (itemFetchErr || !itemAtual) throw new Error("Item não encontrado");

      // 🔒 TRAVA DA TRIAGEM (vale para TODOS, inclusive master): toda coluna com
      // triagem só se move pela própria triagem, em qualquer quadro. Fecha de uma
      // vez o selo de status, o arrastar do card e o botão de ação do BKO — os
      // três moviam por fora e não entravam no relatório do BKO.
      {
        const nomeTriagem = getTriagemDaColuna(itemAtual.quadro_atual || '', itemAtual.coluna_atual || '');
        if (nomeTriagem && !args.origemTriagem) {
          throw new Error(`🔒 Pedidos em ${itemAtual.coluna_atual} só se movem pela ${nomeTriagem} (clique no cabeçalho da coluna no Kanban).`);
        }
      }

      const { data: updateResult, error: updateErr } = await supabase
        .from("itens_pedido")
        .update({
          quadro_atual: quadroDestino,
          coluna_atual: colunaDestino,
          status_id: statusIdDestino,
          entrada_coluna_em: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", args.itemId)
        .select("id");
      
      if (updateErr) throw updateErr;
      if (!updateResult || updateResult.length === 0) {
        throw new Error("⚠️ Erro ao atualizar item.");
      }

      // Registrar no histórico (MODO FANTASMA: Master grava em campo de auditoria)
      const isMasterUser = userRole === 'master';
      const { error: histErr } = await supabase.from("historico_status").insert({
        pedido_id: pedidoId,
        item_id: args.itemId,
        quadro_anterior: itemAtual.quadro_atual,
        coluna_anterior: itemAtual.coluna_atual,
        quadro_atual: quadroDestino,
        coluna_atual: colunaDestino,
        status_atual_id: statusIdDestino,
        motivo: motivo || "Movido via sistema (item individual)",
        user_id: user.id,
        ...(isMasterUser ? { executado_por_master_id: user.id } : {}),
      } as any);
      if (histErr) console.error("Erro ao registrar histórico:", histErr);

      if (quadroDestino === "CONTRATO_PRIME" && pedidoAtual.cliente_id) {
        await supabase.rpc("assign_owner_bko_if_needed", { p_cliente_id: pedidoAtual.cliente_id });
      }

      console.log('✅ [usePedidoMovement] Movimentação concluída com sucesso');
      
      } catch (error: any) {
        console.error('❌ [usePedidoMovement] ERRO DETALHADO:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
          stack: error.stack
        });
        throw error;
      }
    },
    onSuccess: async (data, variables) => {
      console.log('✅ [usePedidoMovement] onSuccess - Invalidando queries...');
      
      // ✅ Invalidar queries do pedido para refetch quando reaberto
      qc.invalidateQueries({ 
        queryKey: ['pedido-detail', variables.pedidoId]
      });
      
      qc.invalidateQueries({ 
        queryKey: ['pedido-itens-completo', variables.pedidoId]
      });

      // ✅ Invalidar TODOS os kanbans (todas as queries, independente de filtros)
      qc.invalidateQueries({ queryKey: ['kanban'], exact: false });
      qc.invalidateQueries({ queryKey: ['kanban-optimized'], exact: false });
      
      // ✅ Invalidar apenas queries essenciais do pedido
      qc.invalidateQueries({ queryKey: ['pedido', variables.pedidoId] });
      qc.invalidateQueries({ queryKey: ['planilha-itens'], exact: false });
      
      // ✅ Invalidar dados do item específico (para Pedido 360)
      if (variables.itemId) {
        qc.invalidateQueries({ queryKey: ['item-completo', variables.itemId] });
      }
      
      // ✅ Invalidar view materializada da planilha
      qc.invalidateQueries({ queryKey: ['v_planilha_mae'], exact: false });
      
      // ✅ Invalidar timeline do pedido para atualizar movimentos
      qc.invalidateQueries({ queryKey: ['timeline-pedido', variables.pedidoId] });
      
      // ✅ Invalidar painel 360 para refletir mudanças de status
      qc.invalidateQueries({ queryKey: ['pedidos-cliente-visao360'] });
      
      // ✅ Invalidar histórico da discadora para refletir mudanças de status dos itens
      qc.invalidateQueries({ queryKey: ['discadora-historico-tabulacao'], exact: false });
      
      await efeitosDepoisDeMover(variables);

      // ✅ NÃO chamar invalidateAll() - causa sobrecarga de 54+ queries
      console.log('✅ [usePedidoMovement] Queries invalidadas com sucesso!');
    },
    onError: (error: Error, variables, context) => {
      console.error('🔴 [usePedidoMovement] onError - Iniciando rollback completo');
      
      // ✅ ROLLBACK COMPLETO - Restaurar TODAS as queries salvas
      if (context?.previousItens) {
        qc.setQueryData(['pedido-itens-completo', variables.pedidoId], context.previousItens);
      }
      if (context?.previousPedidoDetail) {
        qc.setQueryData(['pedido-detail', variables.pedidoId], context.previousPedidoDetail);
      }
      if (context?.previousKanbanQueries) {
        context.previousKanbanQueries.forEach(({ queryKey, data }) => {
          qc.setQueryData(queryKey, data);
        });
      }
      
      // ✅ Invalidar TODOS os kanbans para forçar refresh
      qc.invalidateQueries({ queryKey: ['kanban'], exact: false });
      qc.invalidateQueries({ queryKey: ['kanban-optimized'], exact: false });
      
      console.log('✅ [usePedidoMovement] Rollback completo executado');
      
      // Detectar erro de validação do DB trigger
      if (error.message.includes('VALIDACAO_ITENS_FALHOU')) {
        const jsonMatch = error.message.match(/\[.*\]/);
        if (jsonMatch) {
          try {
            const erros = JSON.parse(jsonMatch[0]);
            const msg = erros.map((e: any) => `• ${e.item}: ${e.erro}`).join('\n');
            toast({
              variant: "destructive",
              title: "❌ Corrija os itens antes de mover",
              description: msg,
            });
            return;
          } catch (parseError) {
            // Se falhar o parse, continua com erro genérico
          }
        }
      }

      toast({ variant: "destructive", title: "Erro ao mover pedido", description: error.message });
    },
  });

  /* O que acontece depois que uma linha muda de coluna: o aviso de ACEITE ao
     consultor e o disparo da ativação quando chega ao fim da Qualidade. Roda
     por linha, no movimento linha a linha e no pedido inteiro. */
  async function efeitosDepoisDeMover(variables: { pedidoId: string; itemId?: string; quadroDestino: string; colunaDestino: string }) {
    // 🔔 CRIAR AVISO AUTOMÁTICO para ACEITE
    const colunasAceite = ['ACEITE', 'FORA SUL - ACEITE'];
    if (
      variables.quadroDestino === 'CONTRATO_PRIME' && 
      colunasAceite.includes(variables.colunaDestino) &&
      variables.itemId &&
      profile?.id
    ) {
      console.log('🔔 [usePedidoMovement] Criando aviso ACEITE para consultor...');
      try {
        const { data: avisoId, error: avisoError } = await supabase.rpc('fn_criar_aviso_aceite_consultor', {
          p_item_id: variables.itemId,
          p_pedido_id: variables.pedidoId,
          p_bko_id: profile.id
        });
        
        if (avisoError) {
          console.error('❌ [usePedidoMovement] Erro ao criar aviso ACEITE:', avisoError);
        } else {
          console.log('✅ [usePedidoMovement] Aviso ACEITE criado:', avisoId);
        }
      } catch (err) {
        console.error('❌ [usePedidoMovement] Erro inesperado ao criar aviso:', err);
      }
    }
    
    // 🔄 TRIGGER AUTOMÁTICO: Disparar triagem-ativacao quando item chega em status final de QUALIDADE
    const COLUNAS_FINAIS_QUALIDADE = ['FINALIZADO PEDIDO (100%)', '(PN) CONCLUIDO'];
    if (
      variables.quadroDestino === 'QUALIDADE' &&
      COLUNAS_FINAIS_QUALIDADE.includes(variables.colunaDestino) &&
      variables.itemId
    ) {
      console.log('🔄 [usePedidoMovement] Detectado status final QUALIDADE, disparando triagem-ativacao...');
      try {
        const { data: itemData } = await supabase
          .from('itens_pedido')
          .select(`
            pedido_id,
            pedidos(
              cliente_id,
              clientes(razao_social, cnpj, email),
              contatos_cliente(gestor_celular1, gestor_whatsapp, telefone_principal, whatsapp, gestor_nome)
            )
          `)
          .eq('id', variables.itemId)
          .single();

        if (itemData) {
          const pedidoData = (itemData as any).pedidos;
          const cliente = pedidoData?.clientes;
          const contatos = pedidoData?.contatos_cliente;
          const clienteId = pedidoData?.cliente_id;
          const acao = variables.colunaDestino === '(PN) CONCLUIDO' ? 'pn_concluido' : 'finalizar_ativo';

          // Hierarquia de telefone: gestor_celular1 → gestor_whatsapp → telefone_principal → whatsapp
          const telefoneCandidatos = [
            contatos?.gestor_celular1,
            contatos?.gestor_whatsapp,
            contatos?.telefone_principal,
            contatos?.whatsapp,
          ].filter(Boolean);
          const telefone = telefoneCandidatos[0] || '';
          
          supabase.functions.invoke('triagem-ativacao', {
            body: {
              item_id: variables.itemId,
              pedido_id: variables.pedidoId || (itemData as any).pedido_id,
              cnpj: cliente?.cnpj || '',
              razao_social: cliente?.razao_social || '',
              telefone,
              email: cliente?.email || '',
              cliente_id: clienteId || '',
              status_anterior_id: '',
              coluna_anterior: '',
              quadro_anterior: '',
              acao,
            }
          }).then(res => {
            if (res.error) console.error('[usePedidoMovement] Erro triagem-ativacao:', res.error);
            else console.log('✅ [usePedidoMovement] triagem-ativacao disparado com sucesso');
          }).catch(err => console.error('[usePedidoMovement] Erro ao chamar triagem-ativacao:', err));
        }
      } catch (err) {
        console.error('[usePedidoMovement] Erro ao buscar dados para triagem-ativacao:', err);
      }
    }
  }

  async function moveCard(params: {
    pedidoId: string;
    itemId?: string;
    toQuadro: QuadroEnum;
    toColuna: string;
    toStatusId?: string | null;
    motivo?: string | null;
    origemTriagem?: boolean;
    skipValidacao?: boolean;
  }) {
    console.log('📍 [moveCard] INÍCIO:', params);
    
    try {
      // 1️⃣ Buscar coluna ATUAL antes de mover (para registrar movimento)
      let colunaOrigem: string | null = null;
      
      if (params.itemId) {
        const { data: itemAtual } = await supabase
          .from("itens_pedido")
          .select("coluna_atual, quadro_atual")
          .eq("id", params.itemId)
          .single();
        colunaOrigem = itemAtual?.coluna_atual || null;
        console.log('  📍 Coluna origem:', colunaOrigem);
      }
      
      if (params.itemId && params.toColuna.toUpperCase().startsWith('INPUT REALIZADO') && await itensTemFixa([params.itemId])) {
        await exigirInputFixaAprovado(params.pedidoId);
      }

      // 2️⃣ Executar movimentação no banco
      const result = await moveToMutation.mutateAsync({
        pedidoId: params.pedidoId,
        itemId: params.itemId,
        quadroDestino: params.toQuadro,
        colunaDestino: params.toColuna,
        statusIdDestino: params.toStatusId ?? null,
        motivo: params.motivo ?? null,
        origemTriagem: params.origemTriagem ?? false,
        skipValidacao: params.skipValidacao ?? false,
      });
      
      // ✅ Movimento registrado automaticamente via trigger do banco de dados (tabela historico_status)
      console.log('  ℹ️ Movimento será registrado via trigger do banco de dados');
      
      console.log('✅ [moveCard] SUCESSO:', { itemId: params.itemId });
      return result;
    } catch (error: any) {
      console.error('❌ [moveCard] ERRO:', { itemId: params.itemId, error });
      // ✅ Re-throw para Promise.allSettled capturar
      throw error;
    }
  }

  /* O PEDIDO ANDA INTEIRO (Douglas 10/10/2026): "é o pedido, não o item".
     Uma chamada ao banco move todas as linhas na mesma transação, ou nenhuma
     (fn_mover_pedido_junto). Antes era um moveCard por linha, mais de dez
     consultas cada, e qualquer interrupção deixava o pedido picado.
     arrastar = no Contrato, leva também as linhas do pedido que ficaram para
     trás na esteira. */
  async function moverPedidoJunto(params: {
    pedidoId: string;
    itensIds: string[];
    quadroOrigem: string;
    colunaOrigem: string;
    toQuadro: QuadroEnum;
    toColuna: string;
    motivo?: string | null;
    origemTriagem?: boolean;
    arrastar?: boolean;
  }): Promise<{ movidos: Array<{ item_id: string; quadro: string; coluna: string; coluna_anterior: string; arrastado: boolean }>; total: number; arrastados: number }> {
    const nomeTriagem = getTriagemDaColuna(params.quadroOrigem || '', params.colunaOrigem || '');
    if (nomeTriagem && !params.origemTriagem) {
      throw new Error(`🔒 Pedidos em ${params.colunaOrigem} só se movem pela ${nomeTriagem} (clique no cabeçalho da coluna no Kanban).`);
    }
    const { isValid } = await validateRequiredFields("pedido", `${params.toQuadro}:${params.toColuna}`, {
      pedidoId: params.pedidoId, quadroDestino: params.toQuadro, colunaDestino: params.toColuna, statusIdDestino: true,
    });
    if (!isValid) throw new Error("Campos obrigatórios pendentes para essa transição.");
    if (params.toColuna.toUpperCase().startsWith('INPUT REALIZADO') && await itensTemFixa(params.itensIds)) {
      await exigirInputFixaAprovado(params.pedidoId);
    }

    const { data, error } = await supabase.rpc('fn_mover_pedido_junto', {
      p_pedido_id: params.pedidoId,
      p_coluna_origem: params.colunaOrigem,
      p_quadro_destino: params.toQuadro,
      p_coluna_destino: params.toColuna,
      p_motivo: params.motivo ?? null,
      p_itens: params.itensIds,
      p_arrastar: params.arrastar ?? false,
    });
    if (error) throw new Error(error.message || 'O pedido não andou.');
    const r = (data ?? { movidos: [], total: 0, arrastados: 0 }) as Awaited<ReturnType<typeof moverPedidoJunto>>;

    /* Os avisos de cada linha saem juntos, sem uma esperar a outra (a
       triagem clica e faz, Douglas 29/10/2026). */
    await Promise.all((r.movidos ?? []).map((m) =>
      efeitosDepoisDeMover({ pedidoId: params.pedidoId, itemId: m.item_id, quadroDestino: m.quadro, colunaDestino: m.coluna })));
    qc.invalidateQueries({ queryKey: ['pedido-detail', params.pedidoId] });
    qc.invalidateQueries({ queryKey: ['pedido-itens-completo', params.pedidoId] });
    /* Da triagem, o Kanban e a Planilha ficam marcados e se leem quando
       aparecem (`acaoTriagem.ts`); do Kanban, ele se lê na hora. */
    const telasDeFora = params.origemTriagem ? 'none' : 'active';
    qc.invalidateQueries({ queryKey: ['kanban'], exact: false, refetchType: telasDeFora });
    qc.invalidateQueries({ queryKey: ['kanban-optimized'], exact: false, refetchType: telasDeFora });
    qc.invalidateQueries({ queryKey: ['pedido', params.pedidoId] });
    qc.invalidateQueries({ queryKey: ['planilha-itens'], exact: false, refetchType: telasDeFora });
    qc.invalidateQueries({ queryKey: ['v_planilha_mae'], exact: false, refetchType: telasDeFora });
    qc.invalidateQueries({ queryKey: ['timeline-pedido', params.pedidoId] });
    qc.invalidateQueries({ queryKey: ['pedidos-cliente-visao360'] });
    return r;
  }

  /* A retomada do Pedido Recuperado também anda inteira (Douglas 10/10/2026):
     cada linha volta para onde foi cancelada, todas numa transação
     (fn_retomar_pedido). O destino é calculado no banco pela mesma régua de
     resolverDestinoRetomada; aqui só se conferem os campos obrigatórios de
     cada destino antes de chamar. */
  async function retomarPedidoJunto(params: {
    pedidoId: string;
    itensIds: string[];
    destinos: Array<{ quadro: string; coluna: string }>;
    origemTriagem?: boolean;
    agenda?: string;
  }): Promise<{ movidos: Array<{ item_id: string; quadro: string; coluna: string }>; total: number }> {
    const vistos = new Set<string>();
    for (const d of params.destinos) {
      const contexto = `${d.quadro}:${d.coluna}`;
      if (vistos.has(contexto)) continue;
      vistos.add(contexto);
      const { isValid } = await validateRequiredFields("pedido", contexto, {
        pedidoId: params.pedidoId, quadroDestino: d.quadro, colunaDestino: d.coluna, statusIdDestino: true,
      });
      if (!isValid) throw new Error("Campos obrigatórios pendentes para essa transição.");
    }

    const voltamAoInput = params.itensIds.filter((_, i) => params.destinos[i]?.coluna.toUpperCase().startsWith('INPUT REALIZADO'));
    if (voltamAoInput.length && await itensTemFixa(voltamAoInput)) {
      await exigirInputFixaAprovado(params.pedidoId);
    }

    const { data, error } = await supabase.rpc('fn_retomar_pedido', {
      p_pedido_id: params.pedidoId,
      p_itens: params.itensIds,
      ...(params.agenda ? { p_agenda: params.agenda } : {}),
    });
    if (error) throw new Error(error.message || 'O pedido não foi retomado.');
    const r = (data ?? { movidos: [], total: 0 }) as Awaited<ReturnType<typeof retomarPedidoJunto>>;

    /* Os avisos de cada linha saem juntos, sem uma esperar a outra (a
       triagem clica e faz, Douglas 29/10/2026). */
    await Promise.all((r.movidos ?? []).map((m) =>
      efeitosDepoisDeMover({ pedidoId: params.pedidoId, itemId: m.item_id, quadroDestino: m.quadro, colunaDestino: m.coluna })));
    qc.invalidateQueries({ queryKey: ['pedido-detail', params.pedidoId] });
    qc.invalidateQueries({ queryKey: ['pedido-itens-completo', params.pedidoId] });
    /* Da triagem, o Kanban e a Planilha ficam marcados e se leem quando
       aparecem (`acaoTriagem.ts`); do Kanban, ele se lê na hora. */
    const telasDeFora = params.origemTriagem ? 'none' : 'active';
    qc.invalidateQueries({ queryKey: ['kanban'], exact: false, refetchType: telasDeFora });
    qc.invalidateQueries({ queryKey: ['kanban-optimized'], exact: false, refetchType: telasDeFora });
    qc.invalidateQueries({ queryKey: ['pedido', params.pedidoId] });
    qc.invalidateQueries({ queryKey: ['planilha-itens'], exact: false, refetchType: telasDeFora });
    qc.invalidateQueries({ queryKey: ['v_planilha_mae'], exact: false, refetchType: telasDeFora });
    qc.invalidateQueries({ queryKey: ['timeline-pedido', params.pedidoId] });
    qc.invalidateQueries({ queryKey: ['pedidos-cliente-visao360'] });
    return r;
  }

  return { moveTo: moveToMutation.mutateAsync, moveCard, moverPedidoJunto, retomarPedidoJunto, isMoving: moveToMutation.isPending };
}
